"use client";

import Heading from "@afa-shared/afa-components/dist/Heading";
import ListCard from "@afa-shared/afa-components/dist/ListCard";
import FundingListCard from "@components/FundingListCard/FundingListCard";
import { ContentLink } from "@components/Link/ContentLink";
import { StandardLink } from "@components/Link/StandardLink";
import LinkedHeading from "@components/LinkedHeading/LinkedHeading";
import { ListBlockType_listBlock } from "@components/ListBlock/queries/contentfulTypes/ListBlockType";
import IvSmallTeaserImage from "@components/Medias/Image/IvSmallTeaserImage";
import IvTeaserImage from "@components/Medias/Image/IvTeaserImage";
import Message from "@components/Message/Message";
import PaginationBlock from "@components/Pagination";
import RichTextRenderer from "@components/RichTextRenderer";
import { BlockSize } from "@components/cFContentArea";
import Grid from "@components/client/Grid";
import clsx from "clsx";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import React, { CSSProperties, Suspense, useRef } from "react";

import { PaginationContainer } from "types/ListItem";
import { Context } from "types/context/Context";
import { HeadingContext } from "types/context/HeadingContext";

import pageListStyles from "./pageList.module.css";

export interface ListBlockFull extends ListBlockType_listBlock {
  listresult: PaginationContainer;
}

interface PageListProps {
  listBlock: ListBlockFull;
  uniqueKey?: string;
  context?: Context;
}

type ListType = "evenemang" | "utlysningar:aktuella" | "utlysningar:kommande" | null;

function getListType(block?: ListBlockFull): ListType {
  const contentTypes: string[] = (block as ListBlockFull)?.contentTypes || [];

  if (contentTypes.length === 1 && contentTypes[0].toLowerCase() === "evenemang") {
    return "evenemang";
  }

  if (contentTypes.length === 1 && contentTypes[0].toLowerCase() === "utlysningar") {
    const filters: string[] = (block as ListBlockFull).fundingPageFilter || [];
    if (filters.length === 1 && filters[0] === "Aktuella utlysningar") {
      return "utlysningar:aktuella";
    }
    if (filters.length === 1 && filters[0] === "Kommande utlysningar") {
      return "utlysningar:kommande";
    }
  }

  return null;
}

function getEmptyText(listType: ListType): string {
  switch (listType) {
    case "evenemang":
      return "Just nu har vi inga inplanerade evenemang. Återkom gärna vid ett senare tillfälle.";
    case "utlysningar:aktuella":
      return "Just nu har vi inga aktuella utlysningar.";
    case "utlysningar:kommande":
      return "Information om kommande utlysningar kommer inom kort.";
    default:
      return "Just nu finns det inga aktuella uppdateringar.";
  }
}

export const PageList = ({ listBlock, uniqueKey, context }: PageListProps) => {
  const listRef = useRef(null);
  const listItems = listBlock?.listresult;
  const resultItems = listItems?.listResult;
  const totalItems = listItems?.totalItems;
  const columnSize = resultItems?.length > 2 ? 4 : resultItems?.length === 2 ? 6 : 12;
  const columnWidth = 100 / (12 / columnSize);
  const linkOnPageList = listBlock?.linkOnList;
  const usePagination =
    listBlock?.pagination && resultItems && totalItems > 0 && totalItems > listBlock?.numberOfItems;
  const linkedHeading = listBlock?.linkPlacement === "Länk i rubrik";
  const linkedButton =
    listBlock?.linkPlacement === "Knapp i botten" || listBlock?.linkPlacement === null;
  const regularLink = listBlock?.linkPlacement === "Länk i botten";

  const url = linkOnPageList?.url;

  const listType = getListType(listBlock);
  const emptyText = getEmptyText(listType);

  const hiddenForPagetype = (type: string) => {
    return type === "Landingpage" || type === "Standardpage" || type === "Insurancepage";
  };

  const trackNewsAndEvents = (type: string): "news" | undefined => {
    if (type === "Newspage" || type === "Eventpage") {
      return "news";
    }
    return undefined;
  };
  const useMinimalDesign = !!listBlock?.minimalDesign;

  const getNewsType = (type: string): any => {
    switch (type) {
      case "Eventpage":
        return "event";
      case "pressrelease":
        return "pressrelease";
      default:
        return "default";
    }
  };

  const getLinkText = (item) => {
    // For news, events, press releases, and reports, no link text should be shown in the card
    if (
      item?.type === "Newspage" ||
      item?.type === "Eventpage" ||
      item?.type === "Reportpage" ||
      item?.type === "pressrelease"
    ) {
      return "";
    }
    // For other types, use the explicit linkText field if available
    return item?.linkText || "";
  };

  const getDateAndTimeArray = (item) => {
    const dateAndTimeArray = item?.dateAndTimeArray;
    if (getNewsType(item?.type) === "event" && !item?.isOngoingEvent) {
      return ["Genomfördes " + dateAndTimeArray[0]];
    }
    if (useMinimalDesign) return [dateAndTimeArray[1]];
    return [dateAndTimeArray[0]];
  };

  const headingContext: HeadingContext = {
    parentLevel: context?.heading?.parentLevel,
    childLevel: listBlock?.heading ? context?.heading?.childLevel : context?.heading?.parentLevel,
  };
  context = { ...context, heading: headingContext };

  const listText = () => (
    <>
      {listBlock?.heading && (
        <div className={pageListStyles.headingWrapper}>
          {linkOnPageList && linkedHeading && resultItems ? (
            <LinkedHeading
              url={url}
              headingLevel={context?.heading?.parentLevel}
              heading={listBlock?.heading}
            />
          ) : (
            <Heading
              variant={context?.heading?.parentLevel}
              removeMargin
              children={listBlock?.heading}
            />
          )}
        </div>
      )}
      {listBlock?.text && (
        <RichTextRenderer
          json={listBlock?.text?.json}
          links={listBlock?.text?.links}
          context={context}
        />
      )}
    </>
  );

  const listStyles: CSSProperties = {
    "--column-width": `${columnWidth}%`,
  } as CSSProperties;

  return resultItems?.length > 0 ? (
    <div
      className={clsx(pageListStyles.pageList, {
        "full-width":
          context?.sizes?.desktop === BlockSize.Full || context?.sizes?.notebook === BlockSize.Full,
      })}
      style={listStyles}
      data-testid="page-list"
      data-contentful-entry-id={listBlock.sys?.id}
      data-contentful-field-id="name"
    >
      {listBlock?.heading && listBlock?.text ? (
        <div className={pageListStyles.textWrapper}>{listText()}</div>
      ) : (
        listText()
      )}
      {listBlock?.listStyle === "Lista" ? (
        <ul
          className={clsx(pageListStyles.list, pageListStyles.column, {
            [pageListStyles.border]: !context?.sectionBlock?.coloredSection,
          })}
          ref={listRef}
          tabIndex={-1}
          data-temp-keep-index
        >
          {resultItems?.map((item, index) => {
            const url = item?.url;
            const hideDate = item?.hideDate || (useMinimalDesign && item?.type !== "Eventpage");
            if (item.isFundingPageItem) {
              return (
                <li
                  className={clsx(pageListStyles.listItem, {
                    [pageListStyles.minimalDesignItem]: useMinimalDesign,
                  })}
                  key={index}
                >
                  <ContentLink
                    href={url || ""}
                    className={clsx(pageListStyles.columnLink, pageListStyles.fundingLink)}
                    trackingData={{
                      sectionTitle:
                        context?.sectionBlock?.sectionBlockHeading ??
                        listBlock?.heading ??
                        context?.pageHeading ??
                        undefined,
                      sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
                    }}
                    contentText={item?.heading}
                  >
                    <FundingListCard
                      nextImage={
                        item?.ivTeaserImage?.[0] && (
                          <IvTeaserImage
                            image={item?.ivTeaserImage?.[0]}
                            pressImageUrl={item?.imageUrl}
                            pressImageAltText={item?.imageAltText}
                          />
                        )
                      }
                      heading={item?.heading}
                      headingVariant={context?.heading?.childLevel}
                      text={!useMinimalDesign && item?.text}
                      fundingOpeningDate={item?.fundingOpeningDate}
                      fundingClosingDate={item?.fundingClosingDate}
                    />
                  </ContentLink>
                </li>
              );
            }
            return (
              <li
                className={clsx(
                  {
                    [pageListStyles.minimalDesignListItem]: useMinimalDesign,
                  },
                  pageListStyles.listItem,
                )}
                key={index}
              >
                <ContentLink
                  href={url || ""}
                  prefetch={false}
                  className={pageListStyles.columnLink}
                  trackingData={{
                    sectionTitle:
                      context?.sectionBlock?.sectionBlockHeading ??
                      listBlock?.heading ??
                      context?.pageHeading ??
                      undefined,
                    sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
                    ...(trackNewsAndEvents(item?.type) && {
                      linkType: trackNewsAndEvents(item?.type),
                    }),
                  }}
                  contentText={item?.heading}
                >
                  <ListCard
                    nextImage={
                      <IvSmallTeaserImage
                        image={item?.ivTeaserImage?.[0]}
                        pressImageUrl={item?.imageUrl}
                        pressImageAltText={item?.imageAltText}
                        className="smallTeaserImage"
                      />
                    }
                    key={`card-${uniqueKey}`}
                    direction={"row"}
                    heading={item?.heading}
                    headingVariant={context?.heading?.childLevel}
                    text={!useMinimalDesign && item?.text}
                    categories={!useMinimalDesign && item?.categories}
                    variant={getNewsType(item?.type)}
                    dateSignDay={item?.dateSignDay}
                    dateSignMonth={item?.dateSignMonth}
                    isOngoingEvent={item?.isOngoingEvent}
                    dateAndTimeArray={getDateAndTimeArray(item)}
                    hideDate={hideDate || hiddenForPagetype(item?.type)}
                    linkText={getLinkText(item)}
                    className={clsx({
                      [pageListStyles.minimalDesignItem]: useMinimalDesign,
                    })}
                  />
                </ContentLink>
              </li>
            );
          })}
          {linkOnPageList && regularLink && resultItems && (
            <StandardLink
              type={"arrow_forward"}
              text={linkOnPageList?.linkText}
              href={url}
              variant="link"
              trackingData={{
                sectionTitle:
                  context?.sectionBlock?.sectionBlockHeading ??
                  listBlock?.heading ??
                  context?.pageHeading ??
                  undefined,
                sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
              }}
            />
          )}
        </ul>
      ) : (
        <>
          <ul
            className={clsx(pageListStyles.list, pageListStyles.row, {
              [pageListStyles.border]: !context?.sectionBlock?.coloredSection,
            })}
            ref={listRef}
            tabIndex={-1}
            data-temp-keep-index
          >
            {resultItems?.map((item, index) => {
              return (
                <li className={pageListStyles.listItem} key={index}>
                  <ContentLink
                    href={item?.url || ""}
                    prefetch={false}
                    className={pageListStyles.rowLink}
                    trackingData={{
                      sectionTitle:
                        context?.sectionBlock?.sectionBlockHeading ??
                        listBlock?.heading ??
                        context?.pageHeading ??
                        undefined,
                      sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
                      ...(trackNewsAndEvents(item?.type) && {
                        linkType: trackNewsAndEvents(item?.type),
                      }),
                    }}
                    contentText={item?.heading}
                  >
                    <ListCard
                      nextImage={
                        <IvTeaserImage
                          image={item?.ivTeaserImage?.[0]}
                          pressImageUrl={item?.imageUrl}
                          pressImageAltText={item?.imageAltText}
                        />
                      }
                      key={`card-${uniqueKey}`}
                      direction={"column"}
                      heading={item?.heading}
                      headingVariant={context?.heading?.childLevel}
                      text={item?.text}
                      categories={item?.categories}
                      variant={getNewsType(item?.type)}
                      dateSignDay={item?.dateSignDay}
                      dateSignMonth={item?.dateSignMonth}
                      isOngoingEvent={item?.isOngoingEvent}
                      dateAndTimeArray={getDateAndTimeArray(item)}
                      hideDate={item?.hideDate || hiddenForPagetype(item?.type)}
                      linkText={getLinkText(item)}
                      data-contentful-entry-id={item.id}
                      data-contentful-field-id="name"
                    />
                  </ContentLink>
                </li>
              );
            })}
          </ul>
          {linkOnPageList && regularLink && resultItems && (
            <StandardLink
              type={"arrow_forward"}
              text={linkOnPageList?.linkText}
              href={url}
              variant="link"
              trackingData={{
                sectionTitle:
                  context?.sectionBlock?.sectionBlockHeading ??
                  listBlock?.heading ??
                  context?.pageHeading ??
                  undefined,
                sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
              }}
            />
          )}
        </>
      )}
      {usePagination && (
        <Suspense>
          <NuqsAdapter>
            <PaginationBlock
              totalItems={totalItems}
              navbarLabel={"Paginering för sidlistning"}
              itemsPerPage={listBlock?.numberOfItems}
              listRef={listRef}
            />
          </NuqsAdapter>
        </Suspense>
      )}
      {linkOnPageList && linkedButton && resultItems && (
        <Grid direction="row" justifyContent="center">
          <StandardLink
            type={"arrow_forward"}
            text={linkOnPageList?.linkText}
            href={url}
            variant="button"
            buttonStyle="secondary"
            trackingData={{
              sectionTitle:
                context?.sectionBlock?.sectionBlockHeading ??
                listBlock?.heading ??
                context?.pageHeading ??
                undefined,
              sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
            }}
          />
        </Grid>
      )}
    </div>
  ) : (
    <div className={pageListStyles.fallbackWrapper}>
      {listBlock?.heading && listBlock?.text ? (
        <div className={pageListStyles.textWrapper}>{listText()}</div>
      ) : (
        listText()
      )}
      <Message type="info" heading={emptyText} headingVariant={context?.heading?.childLevel} />
    </div>
  );
};
export default PageList;
